import { useEffect, useRef, useState } from "react";
import { Mail, Upload, Library } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import ImageUpload from "../../components/ImageUpload";
import MediaLibraryModal from "../../components/admin/MediaLibraryModal";
import { useUploadStatus } from "../../context/UploadStatusContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const SITE_FIELDS = [
    ["business_name", "Business Name", "text"],
    ["tagline", "Tagline", "text"],
    ["about_short", "About (short)", "text"],
    ["about_long", "About (long)", "textarea"],
    ["address", "Address", "text"],
    ["email", "Public Email", "text"],
    ["phone", "Phone", "text"],
    ["whatsapp", "WhatsApp", "text"],
    ["instagram_url", "Instagram URL", "text"],
    ["facebook_url", "Facebook URL", "text"],
    ["tiktok_url", "TikTok URL", "text"],
    ["youtube_url", "YouTube URL", "text"],
];

const SMTP_FIELDS = [
    ["smtp_host", "SMTP Host", "text", "mail.cakecentral.com.au"],
    ["smtp_port", "SMTP Port", "number", "587"],
    ["smtp_username", "SMTP Username", "text", "noreply@cakecentral.com.au"],
    ["smtp_password", "SMTP Password", "password", ""],
    ["smtp_from", "From Address", "text", "noreply@cakecentral.com.au"],
    ["notify_email", "Notify Inquiries To", "text", "info@cakecentral.com.au"],
];

const STRIPE_FIELDS = [
    ["stripe_publishable_key", "Publishable Key", "text", "pk_live_… or pk_test_…"],
    ["stripe_secret_key", "Secret Key", "password", "sk_live_… or sk_test_…"],
    ["stripe_webhook_secret", "Webhook Signing Secret", "password", "whsec_…"],
];

const BANNER_FIELDS = [
    ["banner_about", "About Page Banner"],
    ["banner_flavours", "Flavours Page Banner"],
    ["banner_gallery", "Gallery Page Banner"],
    ["banner_shop", "Shop Page Banner"],
    ["banner_blog", "Blog Page Banner"],
    ["banner_contact", "Contact Page Banner"],
];

function HeroImagesSection({ field, title, help, form, setForm }) {
    const [newUrl, setNewUrl] = useState("");
    const [uploading, setUploading] = useState(false);
    const [libraryOpen, setLibraryOpen] = useState(false);
    const fileRef = useRef(null);
    const { begin: beginUpload, end: endUpload } = useUploadStatus();

    const images = form[field] || [];

    const addUrls = (urls) => setForm((f) => ({ ...f, [field]: [...(f[field] || []), ...urls] }));

    const uploadImages = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        beginUpload();
        const urls = [];
        for (const file of files) {
            try {
                const fd = new FormData();
                fd.append("file", file);
                const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                urls.push(r.data.url);
            } catch (err) {
                toast.error(`Failed to upload ${file.name}: ${err.response?.data?.detail || err.message}`);
            }
        }
        if (urls.length) {
            addUrls(urls);
            toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} uploaded`);
        }
        setUploading(false);
        endUpload();
        if (fileRef.current) fileRef.current.value = "";
    };

    return (
        <section className="bg-white border border-[#EAE3DE] p-6 md:p-8 space-y-4">
            <div>
                <h3 className="font-display text-xl mb-1">{title}</h3>
                <p className="text-xs text-ink-soft">{help}</p>
            </div>

            {images.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {images.map((url, i) => (
                        <div key={i} className="relative group aspect-[2/3] overflow-hidden bg-[#F9F5F2]">
                            <img src={url} alt={`${title} ${i + 1}`} className="w-full h-full object-cover object-center" loading="lazy" />
                            <button
                                type="button"
                                onClick={() => setForm({ ...form, [field]: images.filter((_, j) => j !== i) })}
                                className="absolute top-1 right-1 w-5 h-5 bg-black/60 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 rounded-sm"
                                aria-label={`Remove image ${i + 1}`}
                                data-testid={`${field}-remove-${i}`}
                            >
                                ×
                            </button>
                            <span className="absolute bottom-1 left-1 text-[9px] bg-black/50 text-white px-1 rounded leading-tight">
                                {i % 2 === 0 ? "L" : "R"}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={uploadImages}
                    className="hidden"
                    data-testid={`${field}-file-input`}
                />
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="btn-pink disabled:opacity-60 inline-flex items-center gap-2"
                    data-testid={`${field}-upload-btn`}
                >
                    <Upload size={14} />
                    {uploading ? "Uploading…" : "Upload from Computer"}
                </button>
                <button
                    type="button"
                    onClick={() => setLibraryOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-3 border border-[#EAE3DE] text-ink text-[10px] tracking-[0.2em] uppercase hover:border-blush hover:text-blush transition-colors"
                    data-testid={`${field}-library-btn`}
                >
                    <Library size={14} /> Library
                </button>
                <span className="text-xs text-ink-soft">Select multiple at once (JPG, PNG, WEBP)</span>
            </div>

            {libraryOpen && (
                <MediaLibraryModal
                    onSelect={(url) => {
                        addUrls([url]);
                        setLibraryOpen(false);
                    }}
                    onClose={() => setLibraryOpen(false)}
                />
            )}

            <div className="flex gap-2">
                <input
                    type="url"
                    placeholder="https://images.unsplash.com/photo-…"
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            if (!newUrl.trim()) return;
                            addUrls([newUrl.trim()]);
                            setNewUrl("");
                        }
                    }}
                    className="flex-1 px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush text-sm"
                    data-testid={`${field}-url-input`}
                />
                <button
                    type="button"
                    onClick={() => {
                        if (!newUrl.trim()) return;
                        addUrls([newUrl.trim()]);
                        setNewUrl("");
                    }}
                    className="btn-pink shrink-0"
                    data-testid={`${field}-add-btn`}
                >
                    Add URL
                </button>
            </div>
        </section>
    );
}

export default function AdminSettings() {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const { isUploading } = useUploadStatus();

    useEffect(() => {
        api.get("/admin/settings").then((r) => setForm(r.data));
    }, []);

    const save = async (e) => {
        e.preventDefault();
        if (saving || isUploading) return;
        setSaving(true);
        try {
            await api.put("/settings", form);
            toast.success("Settings updated");
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const testEmail = async () => {
        if (testing) return;
        setTesting(true);
        try {
            const r = await api.post("/admin/test-email", { to: form.notify_email || form.email });
            toast.success(`Test email sent to ${r.data.sent_to}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Test failed");
        } finally {
            setTesting(false);
        }
    };

    const renderField = ([k, label, type, placeholder = ""]) => (
        <div key={k}>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">{label}</label>
            {type === "textarea" ? (
                <textarea
                    rows={4}
                    value={form[k] || ""}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="w-full px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush"
                    data-testid={`settings-${k}`}
                />
            ) : (
                <input
                    type={type}
                    placeholder={placeholder}
                    value={form[k] ?? ""}
                    onChange={(e) => setForm({ ...form, [k]: type === "number" ? parseInt(e.target.value) || 0 : e.target.value })}
                    className="w-full px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush"
                    data-testid={`settings-${k}`}
                />
            )}
        </div>
    );

    return (
        <div data-testid="admin-settings" className="space-y-8 max-w-3xl">
            <div>
                <h2 className="font-display text-3xl mb-1">Site Settings</h2>
                <p className="text-sm text-ink-soft">Public-facing info, social links, and email notifications.</p>
            </div>

            <form onSubmit={save} className="space-y-8">
                <section className="bg-white border border-[#EAE3DE] p-6 md:p-8 space-y-4">
                    <h3 className="font-display text-xl mb-2">Public</h3>
                    {SITE_FIELDS.map(renderField)}
                </section>

                <HeroImagesSection
                    field="hero_images"
                    title="Hero Images — Desktop"
                    help="Portrait images shown in the desktop hero slider. Recommend 4–8 images. Even-numbered images (1st, 3rd…) appear on the left; odd-numbered on the right."
                    form={form}
                    setForm={setForm}
                />

                <HeroImagesSection
                    field="hero_images_mobile"
                    title="Hero Images — Mobile"
                    help="Optional separate image set for mobile screens. Leave empty to reuse the desktop images above."
                    form={form}
                    setForm={setForm}
                />

                {/* ── Page Banners ─────────────────────────────────────────────────── */}
                <section className="bg-white border border-[#EAE3DE] p-6 md:p-8 space-y-6">
                    <div>
                        <h3 className="font-display text-xl mb-1">Page Banners</h3>
                        <p className="text-xs text-ink-soft">
                            Banner image shown at the top of each page. Leave blank to use the default image.
                        </p>
                    </div>
                    {BANNER_FIELDS.map(([k, label]) => (
                        <ImageUpload
                            key={k}
                            label={label}
                            value={form[k] || ""}
                            onChange={(v) => setForm({ ...form, [k]: v })}
                            testIdPrefix={k}
                        />
                    ))}
                </section>

                {/* ── Delivery ─────────────────────────────────────────────────────── */}
                <section className="bg-white border border-[#EAE3DE] p-6 md:p-8 space-y-4">
                    <div>
                        <h3 className="font-display text-xl mb-1">Shop Delivery (AUD)</h3>
                        <p className="text-xs text-ink-soft">
                            Flat delivery fee applied to shop orders. Set free-delivery threshold to $0 to disable free-delivery.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Delivery Fee (AUD)</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="12.00"
                                value={form.delivery_fee ?? ""}
                                onChange={(e) => setForm({ ...form, delivery_fee: parseFloat(e.target.value) || 0 })}
                                className="w-full px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush"
                                data-testid="settings-delivery_fee"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Free Delivery Above (AUD)</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="100.00"
                                value={form.free_delivery_min ?? ""}
                                onChange={(e) => setForm({ ...form, free_delivery_min: parseFloat(e.target.value) || 0 })}
                                className="w-full px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush"
                                data-testid="settings-free_delivery_min"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Pickup Address</label>
                        <input
                            type="text"
                            placeholder="Hinchinbrook, NSW"
                            value={form.pickup_address ?? ""}
                            onChange={(e) => setForm({ ...form, pickup_address: e.target.value })}
                            className="w-full px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush"
                            data-testid="settings-pickup_address"
                        />
                        <p className="text-xs text-ink-soft mt-1">Shown to customers who choose "Pickup" at checkout (e.g. Bento Cakes).</p>
                    </div>
                </section>

                {/* ── SMTP ─────────────────────────────────────────────────────────── */}
                <section className="bg-white border border-[#EAE3DE] p-6 md:p-8 space-y-4">
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <h3 className="font-display text-xl">Email Notifications (SMTP)</h3>
                            <p className="text-xs text-ink-soft mt-1">
                                Use your own SMTP server. Common ports: 587 (STARTTLS), 465 (SSL).
                                Leave blank to disable email notifications.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={testEmail}
                            disabled={testing || !form.smtp_host}
                            className="btn-pink disabled:opacity-50"
                            data-testid="test-email-btn"
                        >
                            <Mail size={14} /> {testing ? "Sending…" : "Send Test"}
                        </button>
                    </div>
                    {SMTP_FIELDS.map(renderField)}
                    <label className="flex items-center gap-2 pt-2">
                        <input
                            type="checkbox"
                            checked={form.smtp_use_tls ?? true}
                            onChange={(e) => setForm({ ...form, smtp_use_tls: e.target.checked })}
                            data-testid="settings-smtp_use_tls"
                        />
                        <span className="text-sm">Use STARTTLS (recommended for port 587)</span>
                    </label>
                </section>

                {/* ── Payments (Stripe) ────────────────────────────────────────────── */}
                <section className="bg-white border border-[#EAE3DE] p-6 md:p-8 space-y-4">
                    <div>
                        <h3 className="font-display text-xl mb-1">Payments (Stripe)</h3>
                        <p className="text-xs text-ink-soft mt-1">
                            Enables the "Card (Stripe)" option at Shop checkout. Get your keys from the{" "}
                            <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-blush hover:underline">
                                Stripe Dashboard → API keys
                            </a>.
                            After saving, add a webhook endpoint in Stripe pointing at{" "}
                            <code className="text-[11px] bg-[#F9F5F2] px-1 py-0.5">{`${BACKEND_URL}/api/webhooks/stripe`}</code>{" "}
                            (listening for <code className="text-[11px] bg-[#F9F5F2] px-1 py-0.5">checkout.session.completed</code>) and paste its signing secret below.
                        </p>
                    </div>
                    {STRIPE_FIELDS.map(renderField)}
                </section>

                <button type="submit" disabled={saving || isUploading} className="btn-pink disabled:opacity-60" data-testid="save-settings">
                    {isUploading ? "Waiting for upload to finish…" : saving ? "Saving…" : "Save Settings"}
                </button>
            </form>
        </div>
    );
}
